import { useState, useEffect } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputAdornment,
	TextField,
	DialogContentText,
	FormControlLabel,
	Checkbox,
} from "@mui/material";
import "dayjs/locale/en";
import "dayjs/locale/pl";
import { useTranslation } from "react-i18next";

export default function AddOrEditBudgetModal({
	isOpen,
	onClose,
	initialData,
	onSubmit,
}) {
	const { t } = useTranslation();
	const userLanguage = localStorage.getItem("i18nextLng");
	const userCurrency = localStorage.getItem("userCurrency");

	const [amount, setAmount] = useState(initialData.amount);
	const [recurring, setRecurring] = useState(initialData.recurring);
	const [errorAmount, setErrorAmount] = useState("");

	useEffect(() => {
		if (isOpen) {
			setAmount(initialData.amount);
			setRecurring(initialData.recurring);
		}
	}, [isOpen, initialData.amount, initialData.recurring]);

	function capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	const dateOptions = {
		year: "numeric",
		month: "long",
	};

	const date =
		initialData?.date instanceof Date
			? initialData.date
			: new Date(initialData.date);
	const periodName = capitalizeFirstLetter(
		date.toLocaleDateString(userLanguage, dateOptions),
	);

	const validateInputs = () => {
		let isValid = true;
		setErrorAmount("");
		if (!amount && amount !== 0) {
			isValid = false;
			setErrorAmount(t("enterBudgetAmount"));
		} else if (initialData.totalBudget && amount < initialData.minAmount) {
			isValid = false;
			setErrorAmount(
				t("budgetAmountCannotBeLowerThanMinimum") +
					` (${initialData.minAmount} ${userCurrency})`,
			);
		} else if (
			!initialData.totalBudget &&
			initialData.maxAmount !== null &&
			initialData.maxAmount !== undefined &&
			amount > initialData.maxAmount
		) {
			isValid = false;
			setErrorAmount(
				t("maxAmountExceeded") +
					`${initialData.maxAmount} ${userCurrency}`,
			);
		}

		return isValid;
	};

	const handleBalanceChange = (event) => {
		const value = event.target.value;
		const formattedValue = value
			.replace(/[^0-9.]/g, "")
			.replace(/(\.\d{2})\d+/, "$1");
		setAmount(formattedValue);
	};

	const submitForm = async (event) => {
		event.preventDefault();
		if (errorAmount) {
			return;
		}
		if (
			amount === initialData.amount &&
			recurring === initialData.recurring
		) {
			onClose();
			return;
		}
		const dataToSubmit = {
			...initialData,
			amount: parseFloat(amount),
			recurring,
			currency: userCurrency,
			period: "monthly",
		};
		onSubmit(dataToSubmit);
		onClose();
	};

	const handleClose = () => {
		setErrorAmount("");
		onClose();
	};

	return (
		<>
			<Dialog
				disableEnforceFocus
				open={isOpen}
				onClose={onClose}
				sx={{ margin: "auto", padding: 0 }}
			>
				<DialogTitle>
					{initialData.budgetId ? t("editBudget") : t("addBudget")}
				</DialogTitle>
				<form
					noValidate
					onSubmit={async (event) => {
						submitForm(event);
					}}
				>
					<DialogContent sx={{ width: 500 }}>
						{initialData.categoryGroupName && (
							<DialogContentText>
								{t("budgetCategoryGroup")}:{" "}
								{initialData.categoryGroupName}
								{initialData.totalBudget ? "- Total" : ""}
							</DialogContentText>
						)}
						{initialData.categoryName && (
							<DialogContentText>
								{t("budgetCategory")}:{" "}
								{initialData.categoryName}
							</DialogContentText>
						)}
						{initialData.walletName && (
							<DialogContentText>
								{t(initialData.type)}
								{initialData.type === "investment" &&
									` ${t("in")}`}
								: {initialData.walletName}
							</DialogContentText>
						)}
						<DialogContentText>
							{t("budgetFrom")}: {periodName}
						</DialogContentText>
						<FormControlLabel
							control={
								<Checkbox
									checked={recurring}
									onChange={(e) =>
										setRecurring(e.target.checked)
									}
								/>
							}
							label={t("recurringForFuturePeriods")}
						/>
						<FormControl
							fullWidth
							sx={{ mt: 1, flex: 1 }}
							size="small"
						>
							<TextField
								required
								autoFocus
								label={t("balance")}
								type="text"
								fullWidth
								error={Boolean(errorAmount)}
								helperText={errorAmount}
								value={amount}
								slotProps={{
									input: {
										inputMode: "number",
										endAdornment: (
											<InputAdornment position="end">
												{userCurrency}
											</InputAdornment>
										),
									},
								}}
								onChange={handleBalanceChange}
							/>
						</FormControl>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>{t("cancel")}</Button>
						<Button
							type="submit"
							variant="contained"
							onClick={validateInputs}
						>
							{t("save")}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
